// src/components/HUD/HUDScriptAssistantLogic.js
// Chat logic for the HUD ACL Script Builder assistant.
// Sends messages to POST /api/hud/script-assistant and renders
// the response with a copy-to-clipboard button on ACL script blocks.
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const STARTER_PROMPTS = [
  'Flag PHAs where operating transfers exceed 10% of total revenue',
  'Find PHAs with negative net income for A2023',
  'Show all high-risk PHAs (RSK_ID = 1) in Review status',
  'Flag PHAs where accounts payable ratio exceeds threshold',
  'Find PHAs with Due From balance greater than Due To',
  'Show occupancy ratio below 90% for PH Total A2023',
  'Flag PHAs where total liabilities exceed total assets',
  'Find PH CFP submissions with non-zero bookkeeping transfers',
];

// Parse the assistant response and extract ACL script blocks
const parseResponse = (text) => {
  const parts = [];
  const codeRegex = /```acl\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = codeRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'acl', content: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts;
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        padding: '3px 10px',
        fontSize: 10,
        fontWeight: 600,
        borderRadius: 4,
        border: 'none',
        cursor: 'pointer',
        background: copied ? '#1D9E75' : '#534AB7',
        color: '#fff',
        transition: 'background 0.2s',
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
};

const MessageContent = ({ text }) => {
  const parts = parseResponse(text);

  return (
    <div>
      {parts.map((part, i) => {
        if (part.type === 'acl') {
          return (
            <div key={i} style={{ position: 'relative', margin: '8px 0' }}>
              <pre style={{
                background: '#1a1a18',
                color: '#E2E0D8',
                padding: '12px 12px 12px 12px',
                borderRadius: 6,
                fontSize: 11,
                fontFamily: 'monospace',
                overflow: 'auto',
                whiteSpace: 'pre',
                lineHeight: 1.5,
                margin: 0,
              }}>
                {part.content}
              </pre>
              <CopyButton text={part.content} />
            </div>
          );
        }
        return (
          <span key={i} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {part.content}
          </span>
        );
      })}
    </div>
  );
};

const HUDScriptAssistantLogic = ({ onClose }) => {
  const [messages,   setMessages]   = useState([{
    id: 'welcome', from: 'bot',
    text: "I can build ACL scripts for the HUD FDS dataset. Describe what you want to check — for example: \"flag PHAs where the Due To balance exceeds 5% of current liabilities\" — and I'll generate a ready-to-paste script.",
  }]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [history,  setHistory]  = useState([]); // conversation history for context
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userText = text.trim();
    setInput('');
    setMessages(prev => [...prev, { id: Date.now(), from: 'user', text: userText }]);
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_URL}/hud/script-assistant`,
        { message: userText, conversationHistory: history },
        { withCredentials: true }
      );

      const reply = res.data.reply || 'No response received.';

      // Update conversation history for multi-turn context
      setHistory(prev => [
        ...prev,
        { role: 'user',      content: userText },
        { role: 'assistant', content: reply    },
      ].slice(-12)); // keep last 6 turns (12 messages)

      setMessages(prev => [...prev, {
        id: Date.now() + 1, from: 'bot', text: reply,
        isDuplicate: !!res.data.potentialDuplicate,
        duplicateName: res.data.potentialDuplicate,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1, from: 'bot',
        text: `Something went wrong: ${err.response?.data?.error || err.message}`,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setMessages([{
      id: 'welcome', from: 'bot',
      text: "I can build ACL scripts for the HUD FDS dataset. Describe what you want to check and I'll generate a ready-to-paste script.",
    }]);
    setHistory([]);
    setInput('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '80vh' }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid #E2E0D8',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#F7F6F2',
        flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a18' }}>⚙️ ACL Script Builder</div>
          <div style={{ fontSize: 11, color: '#888780', marginTop: 1 }}>Describe what you want to check — I'll write the script</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={resetChat}
            title="Reset chat"
            style={{ background: 'none', border: '1px solid #D3D1C7', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: '#5F5E5A' }}
          >↻</button>
          {onClose && (
            <button
              onClick={onClose}
              style={{ background: 'none', border: '1px solid #D3D1C7', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: '#5F5E5A' }}
            >✕</button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {messages.map(msg => (
          <div key={msg.id} style={{
            display: 'flex',
            justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 10,
          }}>
            <div style={{
              maxWidth: '92%',
              padding: '9px 12px',
              borderRadius: msg.from === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
              background: msg.from === 'user' ? '#534AB7' : '#F7F6F2',
              color: msg.from === 'user' ? '#fff' : '#2C2C2A',
              fontSize: 12,
              lineHeight: 1.5,
              border: msg.from === 'bot' ? '1px solid #E2E0D8' : 'none',
            }}>
              {msg.isDuplicate && (
                <div style={{
                  padding: '6px 10px', borderRadius: 5, marginBottom: 8,
                  background: '#FAEEDA', color: '#633806',
                  border: '1px solid #D48B1A', fontSize: 11, fontWeight: 600,
                }}>
                  ⚠️ Warning: a script named "{msg.duplicateName}" is already registered and may cover similar logic.
                </div>
              )}
              {msg.from === 'bot' ? <MessageContent text={msg.text} /> : msg.text}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
            <div style={{
              padding: '9px 14px', borderRadius: '2px 12px 12px 12px',
              background: '#F7F6F2', border: '1px solid #E2E0D8',
              display: 'flex', gap: 4, alignItems: 'center',
            }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: '#888780', display: 'inline-block',
                  animation: `hudDot 1.2s ${i * 0.2}s ease-in-out infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Starter prompts — shown when chat is at welcome message only */}
      {messages.length <= 1 && (
        <div style={{
          padding: '0 14px 10px',
          display: 'flex', flexWrap: 'wrap', gap: 6, flexShrink: 0,
        }}>
          {STARTER_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => sendMessage(p)}
              disabled={loading}
              style={{
                padding: '5px 10px', fontSize: 10, fontWeight: 500,
                borderRadius: 20, border: '1px solid #D3D1C7',
                background: '#fff', color: '#5F5E5A',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{
        padding: '10px 14px',
        borderTop: '1px solid #E2E0D8',
        display: 'flex', gap: 8, flexShrink: 0,
        background: '#fff',
      }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && sendMessage(input)}
          placeholder="Describe the check you need..."
          disabled={loading}
          style={{
            flex: 1, padding: '8px 10px', fontSize: 12,
            border: '1px solid #D3D1C7', borderRadius: 7,
            outline: 'none', background: '#FAFAF8',
          }}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
          style={{
            padding: '8px 14px', borderRadius: 7, border: 'none',
            fontSize: 12, fontWeight: 600,
            background: loading || !input.trim() ? '#C4C2D8' : '#1D9E75',
            color: '#fff', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          Send
        </button>
      </div>

      <style>{`
        @keyframes hudDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default HUDScriptAssistantLogic;